/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, XCircle, SkipForward, Eye } from 'lucide-react';

interface MobileQuizBannerProps {
  questionText: string;
  score: { correct: number; total: number };
  feedback: 'correct' | 'incorrect' | null;
  hasAnswers: boolean;
  onNext: () => void;
  onShowAnswer: () => void;
  onClear: () => void;
  /** When true, renders as a floating overlay positioned at the top */
  overlay?: boolean;
}

/**
 * MobileQuizBanner - Compact floating quiz UI for mobile devices
 *
 * Displays the question and essential controls in a minimal space
 * so users don't have to scroll between question and fretboard.
 */
const MobileQuizBanner: React.FC<MobileQuizBannerProps> = ({
  questionText,
  score,
  feedback,
  hasAnswers,
  onNext,
  onShowAnswer,
  onClear,
  overlay = false
}) => {
  const accuracy = score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0;

  // Overlay mode: position fixed at top of fretboard area with transparency
  // Inline mode: static card under the tabs
  const overlayClasses = overlay
    ? "fixed top-[113px] left-0 right-0 z-50 mx-2 rounded-lg bg-background/85 backdrop-blur-md border shadow-lg"
    : "w-full bg-card rounded-lg border shadow-sm";

  return (
    <div className={overlayClasses}>
      <div className="px-3 py-2">
        {/* Question and Score Row */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">
              {questionText}
            </p>
          </div>
          <Badge variant={accuracy >= 70 ? "default" : "secondary"} className="shrink-0 text-xs">
            {score.correct}/{score.total}
          </Badge>
        </div>

        {/* Feedback and Controls Row */}
        <div className="flex items-center justify-between gap-2">
          {/* Feedback indicator */}
          <div className="flex items-center gap-1 min-w-0">
            {feedback === 'correct' && (
              <div className="flex items-center gap-1 text-green-600">
                <CheckCircle className="h-4 w-4 shrink-0" />
                <span className="text-xs font-medium">Correct!</span>
              </div>
            )}
            {feedback === 'incorrect' && (
              <div className="flex items-center gap-1 text-red-600">
                <XCircle className="h-4 w-4 shrink-0" />
                <span className="text-xs font-medium">Try again</span>
              </div>
            )}
            {!feedback && hasAnswers && (
              <span className="text-xs text-muted-foreground">Tap fretboard...</span>
            )}
            {!feedback && !hasAnswers && (
              <span className="text-xs text-blue-600">Tap the note on the fretboard</span>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-1 shrink-0">
            {hasAnswers && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onClear}
                className="h-7 px-2 text-xs"
              >
                Clear
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={onShowAnswer}
              className="h-7 px-2 text-xs"
            >
              <Eye className="h-3 w-3 mr-1" />
              Show
            </Button>
            <Button
              size="sm"
              onClick={onNext}
              className="h-7 px-2 text-xs"
            >
              <SkipForward className="h-3 w-3 mr-1" />
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileQuizBanner;
